import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Building2, User } from 'lucide-react'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import type { IClientDetail } from '../../hooks/use-client-detail-query'

interface ClientDetailSummaryProps {
  name: string
  photo?: string | null
  detail: IClientDetail
  companyName: string | null
  agencyName: string | null
}

const fmtDate = (value: string | null) =>
  value ? format(parseISO(value), "d 'de' MMM yyyy", { locale: es }) : null

const TYPE_PERSON_LABEL: Record<string, string> = {
  individual: 'Individual',
  juridical: 'Jurídico'
}

/** Top block of the panel: identity + management data (read-only, presentational). */
export function ClientDetailSummary({
  name,
  photo,
  detail,
  companyName,
  agencyName
}: ClientDetailSummaryProps) {
  const TypeIcon = detail.typePerson === 'juridical' ? Building2 : User
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Avatar seed={detail._id ?? name} className="h-14 w-14 aspect-square">
          {photo ? <AvatarImage className="object-cover" src={photo} alt={name} /> : null}
          <AvatarFallback>{name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight text-primary break-words whitespace-normal">
            {name}
          </p>
          <p className="text-xs text-muted-foreground">C-{String(detail.code).padStart(8, '0')}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <DetailFieldRow label="Empresa" value={companyName} />
        <DetailFieldRow label="Agencia" value={agencyName} />
        <DetailFieldRow
          label="Tipo de persona"
          value={
            <span className="inline-flex items-center gap-1">
              <TypeIcon className="h-3.5 w-3.5" />
              {TYPE_PERSON_LABEL[detail.typePerson] ?? detail.typePerson}
            </span>
          }
        />
        <DetailFieldRow label="Asesor" value={<UserChip name={detail.advisor?.fullname} />} />
        <DetailFieldRow label="Fecha de creación" value={fmtDate(detail.createdAt)} />
        <DetailFieldRow label="Creado por" value={<UserChip name={detail.createdBy?.fullname} />} />
        <DetailFieldRow label="Última actualización" value={fmtDate(detail.updatedAt)} />
        <DetailFieldRow
          label="Actualizado por"
          value={<UserChip name={detail.updatedBy?.fullname} />}
        />
      </div>
    </div>
  )
}
